import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DownloadManagerService } from './download-manager.service';

@Injectable({
  providedIn: 'root'
})
export class BackupService {

  private readonly baseUrl = '/backup';

  // Legacy compatibility
  private lastDownloadedBytesMap = new Map<number, number>();

  constructor(
    private http: HttpClient,
    private downloadManager: DownloadManagerService
  ) {}

  /**
   * Get backups list
   */
  getBackups(businessUnitId: number, page: number, pageSize: number): Observable<any> {
    const params = {
      page: page.toString(),
      per_page: pageSize.toString()
    };
    return this.http.get<any>(this.baseUrl + '/' + businessUnitId, { params });
  }

  /**
   * Create backup
   */
  createBackup(data: any): Observable<any> {
    return this.http.post<any>(this.baseUrl, data);
  }

  /**
   * Update backup
   */
  updateBackup(backupId: string, data: any): Observable<any> {
    return this.http.patch<any>(this.baseUrl + '/' + backupId, data);
  }

  /**
   * Get backup details
   */
  getBackupDetails(backupId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + '/' + backupId);
  }

  /**
   * Start optimized download
   */
  startOptimizedDownload(
    backupId: number,
    url: string,
    filename: string,
    onProgress?: (percent: number, downloadedBytes: number, totalBytes: number) => void,
    onComplete?: () => void,
    onError?: (message: string) => void
  ): void {

    const callbacks: any = {};

    if (onProgress) {
      callbacks.onProgress = function(progress: any) {
        onProgress(progress.progress, progress.downloadedBytes, progress.totalBytes);
      };
    }

    if (onComplete) {
      callbacks.onComplete = function() {
        onComplete();
      };
    }

    if (onError) {
      callbacks.onError = function(id: number, error: string) {
        onError(error);
      };
    }

    this.downloadManager.startDownload(backupId, url, filename, callbacks);
  }

  /**
   * Resume download
   */
  resumeDownload(
    backupId: number,
    url: string,
    filename: string,
    onProgress?: (percent: number, downloadedBytes: number, totalBytes: number) => void,
    onComplete?: () => void,
    onError?: (message: string) => void
  ): void {

    const callbacks: any = {};

    if (onProgress) {
      callbacks.onProgress = function(progress: any) {
        onProgress(progress.progress, progress.downloadedBytes, progress.totalBytes);
      };
    }

    if (onComplete) {
      callbacks.onComplete = function() {
        onComplete();
      };
    }

    if (onError) {
      callbacks.onError = function(id: number, error: string) {
        onError(error);
      };
    }

    this.downloadManager.resumeDownload(backupId, url, filename, callbacks);
  }

  /**
   * Cancel download
   */
  cancelDownload(backupId: number): void {
    this.downloadManager.cancelDownload(backupId);
  }

  /**
   * Pause download
   */
  pauseDownload(backupId: number): void {
    this.downloadManager.pauseDownload(backupId);
  }

  /**
   * Check if download is active
   */
  isDownloadActive(backupId: number): boolean {
    return this.downloadManager.isDownloadActive(backupId);
  }

  /**
   * Get download progress
   */
  getDownloadProgress(backupId: number): any {
    return this.downloadManager.getDownloadProgress(backupId);
  }

  /**
   * Check for active downloads
   */
  hasActiveDownloads(): boolean {
    return this.downloadManager.hasActiveDownloads();
  }

  /**
   * Get all downloads
   */
  getAllDownloads(): Observable<any> {
    return this.downloadManager.getAllDownloads();
  }

  /**
   * LEGACY METHOD - maintained for backward compatibility
   */
  downloadFileViaWorker(
    url: string,
    filename: string,
    backupId: number,
    onProgress: (percent: number, downloadedBytes: number, totalBytes: number) => void
  ): Promise<void> {
    console.warn('downloadFileViaWorker is deprecated. Use startOptimizedDownload instead.');

    return new Promise((resolve, reject) => {
      const callbacks = {
        onProgress: function(progress: any) {
          onProgress(progress.progress, progress.downloadedBytes, progress.totalBytes);
        },
        onComplete: function() {
          resolve();
        },
        onError: function(id: number, error: string) {
          reject({ message: error, downloadedBytes: 0 });
        }
      };

      this.downloadManager.startDownload(backupId, url, filename, callbacks);
    });
  }

  /**
   * Legacy methods
   */
  getLastDownloadedBytes(backupId: number): number {
    const progress = this.downloadManager.getDownloadProgress(backupId);
    return (progress && progress.downloadedBytes) || this.lastDownloadedBytesMap.get(backupId) || 0;
  }

  setLastDownloadedBytes(backupId: number, bytes: number): void {
    this.lastDownloadedBytesMap.set(backupId, bytes);
  }

  resetDownload(backupId: number): void {
    this.lastDownloadedBytesMap.delete(backupId);

    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('download-meta-' + backupId);
    }

    this.downloadManager.cancelDownload(backupId);
  }

  /**
   * Validate backup for download
   */
  validateBackupForDownload(backup: any): { isValid: boolean; message?: string } {
    if (!backup.download_link) {
      return { isValid: false, message: 'Download link not available' };
    }

    if (backup.status !== 'finished') {
      return { isValid: false, message: 'Backup is not ready for download' };
    }

    if (backup.deleted_at) {
      return { isValid: false, message: 'Backup has been deleted' };
    }

    return { isValid: true };
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) { return '0 Bytes'; }

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Estimate download time
   */
  estimateDownloadTime(downloadedBytes: number, totalBytes: number, startTime: number): number {
    if (downloadedBytes === 0 || totalBytes === 0) { return 0; }

    const elapsedTime = (Date.now() - startTime) / 1000;
    const downloadSpeed = downloadedBytes / elapsedTime;
    const remainingBytes = totalBytes - downloadedBytes;

    return Math.ceil(remainingBytes / downloadSpeed);
  }

  /**
   * Get download stats
   */
  getDownloadStats(): any {
    return this.downloadManager.getDownloadStats();
  }
}
